import { Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, BookOpen, Home, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface ErrorStateProps {
  /** Short headline, e.g. "Page not found" */
  title?: string;
  /** One-sentence, human explanation of what happened. */
  description?: string;
  /** Small kicker above the title (status code, section name, …) */
  kicker?: string;
  /** Optional retry handler — renders a "Try again" button when provided. */
  onRetry?: () => void;
  /** "page" fills the viewport, "section" sits inline inside a page. */
  variant?: "page" | "section";
  /** Extra machine-readable marker (used by the CI link checker for soft-404s). */
  dataPage?: string;
  className?: string;
}

/**
 * Single friendly error surface for the whole site: broken routes, failed
 * fetches and crashed sections all land here instead of a blank screen.
 * Always offers a way forward — retry, resources, home, support.
 */
export const ErrorState = ({
  title = "Something went wrong",
  description = "We hit a snag loading this part of the site. It's usually temporary — try again, or jump to the resources library below.",
  kicker,
  onRetry,
  variant = "section",
  dataPage,
  className = "",
}: ErrorStateProps) => {
  const Heading = (variant === "page" ? "h1" : "h2") as "h1" | "h2";

  const body = (
    <div className="mx-auto max-w-xl text-center">
      <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-border bg-muted">
        <AlertTriangle className="h-6 w-6 text-gold" aria-hidden />
      </div>

      {kicker && (
        <div className="mb-2 text-[10px] uppercase tracking-[0.3em] text-gold">{kicker}</div>
      )}

      <Heading className="text-2xl font-bold md:text-3xl">{title}</Heading>
      <p className="mt-3 text-sm text-muted-foreground md:text-base">{description}</p>

      <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
        {onRetry && (
          <Button onClick={onRetry} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
            Try again
          </Button>
        )}
        <Button asChild>
          <Link to="/resources">
            <BookOpen className="mr-2 h-4 w-4" aria-hidden />
            Go to resources
          </Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to="/">
            <Home className="mr-2 h-4 w-4" aria-hidden />
            Home
          </Link>
        </Button>
        <Button asChild variant="ghost">
          <a href="mailto:DrSully@DrSullySpeaks.com">Email support</a>
        </Button>
      </div>

      <button
        type="button"
        onClick={() => window.history.back()}
        className="mt-6 inline-flex items-center text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
      >
        <ArrowLeft className="mr-1 h-3 w-3" aria-hidden />
        Go back
      </button>
    </div>
  );

  if (variant === "page") {
    return (
      <main
        data-page={dataPage}
        data-error-state="page"
        role="alert"
        aria-live="polite"
        className={`flex min-h-screen items-center justify-center bg-background px-4 py-20 ${className}`}
      >
        {body}
      </main>
    );
  }

  return (
    <div
      data-page={dataPage}
      data-error-state="section"
      role="alert"
      aria-live="polite"
      className={`rounded-xl border border-border bg-muted/40 px-6 py-10 ${className}`}
    >
      {body}
    </div>
  );
};

export default ErrorState;
